import PropTypes from 'prop-types';
import React, { useState, createRef } from 'react';
import { useRouter } from 'next/router';
import { withTheme } from 'styled-components';

import {
	H1,
	H4,
	Input,
	MainBody,
	Button,
	BaseLink,
	Breadcrumbs,
	RteContent,
} from 'shared/components';

import { MaxWidthContainer, Spacer, Box } from 'shared/layout';

import { useBlogItem } from 'shared/hooks';

import BlogItem from './BlogItem';

import { postSubscriptionFE } from 'api';
import { DropDown } from '../../components/Dropdown/DropDown';
import Head from 'next/head';

const SubscriptionPage = ({
	theme = {},
	BreadcrumbLinks = {},
	Heading = '',
	Preamble = '',
	NewsLetterBlogHeading = '',
	NewsLetters = [],
	ErrorText = '',
	ThankYouPage = '',
	EmailValidationText = '',
	Link = {},
	SubscriptionCategories = {},
	SearchEngineSettings = {},
	ButtonTextSubscription,
	EmailFieldHeading,
}) => {
	const [inputValue, setInputValue] = useState('');
	const [honeyTrapValue, setHoneyTrapValue] = useState('');
	const [inputError, setInputError] = useState(false);
	const [postError, setPostError] = useState(false);
	const [isValid, setIsValid] = useState(false);
	const [_subscriptionCategories, _setSubscriptionCategories] = useState([]);
	const inputRef = createRef(null);
	const emailValidator =
		/^\w+([.-]?\w+)+@\w+([.:]?\w+)+(.[a-zA-Z0-9]{2,3})+$/;

	const { selectedIds, expandedIds, handleExpand, handleSelect } =
		useBlogItem();
	const { push } = useRouter();

	//Manual validation of email
	const handleInputChange = (e) => {
		if (!e.target.value) {
			setInputError(true);
			setIsValid(false);
		} else {
			setInputError(false);
		}
		setInputValue(e.target.value);
	};

	//Manual validation of email
	const handleBlur = (e) => {
		if (!e.target.value || !emailValidator.test(inputValue)) {
			setInputError(true);
			setIsValid(false);
		} else {
			setInputError(false);
			setIsValid(true);
		}
	};

	//Append queryparam to let thankyou-page know if it is unsubscribe or not
	const handleRedirect = () => {
		push(ThankYouPage);
	};

	const handleSubscribe = async (email) => {
		const _newsLettersToSubscribe = NewsLetters?.filter((x) =>
			selectedIds?.find((y) => y === x.Id),
		).map((x) => x.Id.toString());

		const selectedCategories = _subscriptionCategories
			.map((element) => element.title)
			.join(', ');

		setPostError(false);
		try {
			const response = await postSubscriptionFE({
				email,
				selectedCategories,
				_newsLettersToSubscribe,
			});
			if (response.Success) {
				handleRedirect();
			} else {
				setPostError(true);
			}
		} catch {
			setPostError(true);
		}
	};

	const hasNewsletterOrCategory =
		selectedIds.length > 0 || _subscriptionCategories.length > 0;
	const disabledInput = inputError || !inputValue || !hasNewsletterOrCategory;

	return (
		<>
			<Head>
				{!!SearchEngineSettings.AddNoIndex && (
					<meta name="robots" content="noindex, nofollow" />
				)}
			</Head>

			<MaxWidthContainer narrow>
				<Breadcrumbs breadcrumbLinks={BreadcrumbLinks} />
				{!!Heading && (
					<Spacer>
						<H1>{Heading}</H1>
					</Spacer>
				)}
				{!!Preamble && (
					<Spacer>
						<RteContent text={Preamble} />
					</Spacer>
				)}
				<Spacer>
					<Box max-width={{ lg: theme.spacings[92] }}>
						<DropDown
							identifier="subscription-topic-selection"
							options={SubscriptionCategories?.map(
								({ Id, Heading }) => {
									return { title: Heading, value: Id };
								},
							)}
							placeholder="Välj ämne"
							searchPlaceholder="Sök på ämne"
							isMultiChoice
							onChange={(val) => _setSubscriptionCategories(val)}
							fullWidth
						/>
					</Box>
				</Spacer>
				<Spacer>
					<Spacer spacing={{ base: 7, md: 10 }}>
						<H4>{NewsLetterBlogHeading}</H4>
					</Spacer>
					{!!NewsLetters?.length &&
						NewsLetters?.map((item, ix) => {
							return (
								<BlogItem
									{...item}
									{...{
										selectedIds,
										expandedIds,
										theme,
										handleExpand,
										handleSelect,
									}}
									Id={item.Id}
									key={ix}
									first={ix === 0}
								/>
							);
						})}
				</Spacer>
				{EmailFieldHeading && (
					<Spacer spacing={10}>
						<H4>{EmailFieldHeading}</H4>
					</Spacer>
				)}
				<Spacer>
					<Input
						display="none"
						onChange={(e) => setHoneyTrapValue(e.target.value)}
						value={honeyTrapValue}
					/>
					<Input
						value={inputValue}
						onChange={(e) => handleInputChange(e)}
						onBlur={(e) => handleBlur(e)}
						placeholder="Mejladress"
						type="email"
						error={inputError}
						valid={isValid}
						inputRef={inputRef}
					/>
					{inputError && (
						<MainBody color={theme.colors.error}>
							{EmailValidationText ??
								'Fyll i en korrekt mailadress'}
						</MainBody>
					)}
				</Spacer>
				<Spacer spacing={12}>
					<Box
						display="flex"
						flex-direction={{ base: 'column', md: 'row' }}
						align-items={{ base: 'flex-start', md: 'center' }}
						gap={theme.spacings.default}
					>
						<Box
							display="flex"
							flex-direction="column"
							align-items={{ base: 'flex-start', md: 'flex-end' }}
						>
							<Button
								disabled={disabledInput}
								onClick={() => handleSubscribe(inputValue)}
							>
								{ButtonTextSubscription ?? 'Skicka'}
							</Button>
							{postError && (
								<MainBody
									color={theme.colors.error}
									padding-top={theme.spacings.default}
								>
									{ErrorText ??
										'Något gick fel, vänligen försök igen'}
								</MainBody>
							)}
						</Box>
						{!!Link && (
							<BaseLink
								color={theme.colors.primaryLight}
								href={Link?.Url}
								target={Link?.target}
								font-size={{ md: '1.125rem' }}
							>
								{Link?.Name}
							</BaseLink>
						)}
					</Box>
				</Spacer>
			</MaxWidthContainer>
		</>
	);
};

export default withTheme(SubscriptionPage);

SubscriptionPage.propTypes = {
	BreadcrumbLinks: PropTypes.arrayOf(
		PropTypes.shape({
			CurrentPage: PropTypes.bool,
			IsDirectParent: PropTypes.bool,
			Name: PropTypes.string,
			Url: PropTypes.string,
		}),
	),
	Heading: PropTypes.string,
	Preamble: PropTypes.string,
	NewsLetterBlogHeading: PropTypes.string,
	NewsLetters: PropTypes.arrayOf(
		PropTypes.shape({
			Heading: PropTypes.string,
			Preamble: PropTypes.string,
			Url: PropTypes.string,
			Parameter: PropTypes.string,
		}),
	),
	Blogs: PropTypes.arrayOf(
		PropTypes.shape({
			Heading: PropTypes.string,
			Id: PropTypes.number,
			Preamble: PropTypes.string,
		}),
	),
	EmailValidationText: PropTypes.string,
	ButtonTextSubscription: PropTypes.string,
	EmailFieldHeading: PropTypes.string,
	BlogSignUpUrl: PropTypes.string,
	ErrorText: PropTypes.string,
	BlogUpdateUrl: PropTypes.string,
	ThankYouPage: PropTypes.string,
	Link: PropTypes.object,
	SubscriptionCategories: PropTypes.arrayOf(
		PropTypes.shape({ Heading: PropTypes.string, Id: PropTypes.number }),
	),
};
